import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import PropTypes from 'prop-types';
import { DropdownMenu } from '@3plearning/react-components';
import { t } from '@lingui/macro';
import LocalStorage from '../../helpers/local-storage';
import { FilterListWrapper, FilterWrapper, InputWrapper } from './styled';
import { getClassrooms, getGroups, getStudents } from './filters-util';
import {
  FILTERED_STUDENTS,
  SET_ACTIVE_CLASS,
  SET_ACTIVE_GROUP,
  UPDATE_COURSE_ID,
  UPDATE_SELECTED_TOPICS
} from '../../containers/course-assign/course-assign-types';
import {
  getTopicsFromCourse,
  getTopics
} from '../../containers/course-assign/course-assign-actions';
import { useTracking } from '../../analytics';

const Filters = (props) => {
  const dispatch = useDispatch();
  const { trackClassChanged, trackGroupChanged } = useTracking();
  const filteredStudents = (students) => ({
    type: FILTERED_STUDENTS,
    payload: students
  });
  const { i18n } = props;
  const classrooms = useSelector(
    (state) => state.CourseAssignReducer.classrooms,
    shallowEqual
  );

  const activeClass = useSelector(
    (state) => state.CourseAssignReducer.activeClass
  );
  const activeGroup = useSelector(
    (state) => state.CourseAssignReducer.activeGroup
  );

  const courseTopics = useSelector(
    (state) => state.CourseAssignReducer.courseTopics,
    shallowEqual
  );

  const [classes, setClassrooms] = useState([]);
  const [groups, setGroups] = useState([]);
  const [selectedClass, setDefaultClassOption] = useState(null);
  const [selectedGroup, setDefaultGroupOption] = useState(null);
  const classLabel = i18n._(t('filters.class')`Class`);
  const groupLabel = i18n._(t('filters.group')`Group`);
  const LAST_SELECTED_CLASS = 'LAST_SELECTED_CLASS';

  useEffect(() => {
    const newClassroom = getClassrooms(classrooms);
    if (
      classes?.[0]?.label === newClassroom?.[0]?.label &&
      classes.size === newClassroom?.size
    ) {
      return;
    }
    let lastSelectedClass = LocalStorage.getItem(LAST_SELECTED_CLASS);
    let lastSelectedGroup = LocalStorage.getItem(
      `LAST_SELECTED_GROUP_ClASS_${lastSelectedClass.value}`
    );
    if (classrooms.length) {
      if (
        lastSelectedClass &&
        classrooms.filter((c) => c.classroomId === lastSelectedClass.value)
          .length
      ) {
        setDefaultClassOption(lastSelectedClass);
      } else {
        lastSelectedClass = classrooms[0];
        if (
          selectedClass?.value !== lastSelectedClass?.classroomId ||
          selectedClass?.label !== lastSelectedClass?.name
        ) {
          setDefaultClassOption({
            value: lastSelectedClass && lastSelectedClass.classroomId,
            label: lastSelectedClass && lastSelectedClass.name
          });
        }
      }

      setClassrooms(newClassroom);

      const allGroups = getGroups(
        lastSelectedClass.value || classrooms[0].classroomId,
        classrooms
      );
      setGroups(allGroups);
      if (
        lastSelectedGroup &&
        allGroups.filter((group) => group.value === lastSelectedGroup.value)
          .length
      ) {
        setDefaultGroupOption(lastSelectedGroup);
      } else {
        lastSelectedGroup = allGroups[0];
      }
      dispatch(
        filteredStudents(
          getStudents(
            lastSelectedClass.value || classrooms[0].classroomId,
            lastSelectedGroup.value || allGroups[0].value,
            classrooms
          )
        )
      );
      dispatch({
        type: SET_ACTIVE_CLASS,
        payload: lastSelectedClass.value || classrooms[0].classroomId
      });
      dispatch({
        type: SET_ACTIVE_GROUP,
        payload: lastSelectedGroup.value
      });
    }
  }, [dispatch, classrooms, classes, selectedClass]);

  useEffect(() => {
    let courseID = -1;
    if (activeClass && activeGroup !== '-1') {
      courseID = classrooms
        .find((classroom) => classroom.classroomId === activeClass)
        .groups.find((group) => group.groupId === activeGroup).courseId;
    }
    dispatch({
      type: UPDATE_COURSE_ID,
      payload: { courseID }
    });
    dispatch(getTopicsFromCourse(courseID));
  }, [activeClass, activeGroup, dispatch, classrooms]);

  useEffect(() => {
    dispatch(getTopics(activeClass, activeGroup));
  }, [dispatch, activeClass, activeGroup]);

  useEffect(() => {
    if (courseTopics !== undefined && Object.keys(courseTopics).length > 0) {
      const filterTopics =
        LocalStorage.getItem(
          `LAST_FILTERSEARCH_SELECTION_${activeClass}_${activeGroup}`
        ) ||
        courseTopics.topics.map((topic) => ({
          ...topic,
          selected: true,
          checkedNormal: true,
          checkedEasier: false,
          isHidden: false
        }));
      dispatch({
        type: UPDATE_SELECTED_TOPICS,
        payload: { topics: courseTopics.topics, filterTopics }
      });
    }
  }, [dispatch, courseTopics, activeClass, activeGroup]);

  const onClassroomChange = (option) => {
    trackClassChanged();
    LocalStorage.setItem(LAST_SELECTED_CLASS, option);
    setDefaultClassOption(option);
    const groupList = getGroups(option.value, classrooms);
    setGroups(groupList);

    setDefaultGroupOption(groupList[0]);
    dispatch(
      filteredStudents(
        getStudents(option.value, groupList[0].value, classrooms)
      )
    );
    dispatch({
      type: SET_ACTIVE_CLASS,
      payload: option.value
    });
    dispatch({
      type: SET_ACTIVE_GROUP,
      payload: groupList[0].value
    });
  };

  const onGroupChange = (option) => {
    trackGroupChanged();
    LocalStorage.setItem(
      `LAST_SELECTED_GROUP_ClASS_${selectedClass.value}`,
      option
    );
    setDefaultGroupOption(option);
    dispatch(
      filteredStudents(
        getStudents(selectedClass.value, option.value, classrooms)
      )
    );
    dispatch({
      type: SET_ACTIVE_GROUP,
      payload: option.value
    });
  };

  function GetClassFilters() {
    if (classes instanceof Array && classes.length === 1) {
      return <InputWrapper type="text" value={classes[0].label} readOnly />;
    }
    return (
      <DropdownMenu
        key="ddlClassrooms"
        options={classes}
        selectedOption={selectedClass || classes[0]}
        onSelectOption={onClassroomChange}
      />
    );
  }

  return (
    <FilterListWrapper>
      <FilterWrapper
        data-name="class-selector"
        data-testid="showClassesDropdown"
      >
        <div>{classLabel}</div>
        <GetClassFilters />
      </FilterWrapper>
      <FilterWrapper
        data-name="group-selector"
        data-testid="showGroupsDropdown"
      >
        <div>{groupLabel}</div>
        <DropdownMenu
          key="ddlGroups"
          options={groups}
          selectedOption={selectedGroup || groups[0]}
          onSelectOption={onGroupChange}
        />
      </FilterWrapper>
    </FilterListWrapper>
  );
};

Filters.propTypes = {
  i18n: PropTypes.object
};

Filters.whyDidYouRender = false;

export default Filters;



// WEBPACK FOOTER //
// ./components/filters/filters.js